import { sileo } from 'sileo';

/**
 * Optional action button rendered inside a toast (ex: "Réessayer").
 * Si fourni, remplace le bouton de fermeture par défaut.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Service to manage toast notifications across the application.
 * Uses the sileo library.
 */
export class ToastService {
  /**
   * Show a success toast
   */
  success(title: string, description?: string) {
    let id: string;
    id = sileo.success({
      title,
      description,
      styles: { badge: "bg-green-500/10 text-green-500!" },
      button: { title: '✕', onClick: () => sileo.dismiss(id) },
    });
  }

  /**
   * Show an error toast — supporte optionnellement un bouton d'action
   * (typiquement "Réessayer" sur les erreurs réseau récupérables).
   */
  error(title: string, description?: string, action?: ToastAction) {
    let id: string;
    const button = action
      ? {
          title: action.label,
          onClick: () => {
            sileo.dismiss(id);
            action.onClick();
          },
        }
      : { title: '✕', onClick: () => sileo.dismiss(id) };
    id = sileo.error({
      title,
      description,
      styles: { badge: "bg-red-500/10 text-red-500!" },
      button,
    });
  }

  /**
   * Show a warning toast
   */
  warning(title: string, description?: string) {
    let id: string;
    id = sileo.warning({
      title,
      description,
      styles: { badge: "bg-amber-500/10 text-amber-500!" },
      button: { title: '✕', onClick: () => sileo.dismiss(id) },
    });
  }

  /**
   * Show an info toast — supporte optionnellement un bouton d'action
   * (ex: "Recharger" sur la détection d'une nouvelle version de l'app).
   */
  info(title: string, description?: string, action?: ToastAction) {
    let id: string;
    const button = action
      ? {
          title: action.label,
          onClick: () => {
            sileo.dismiss(id);
            action.onClick();
          },
        }
      : { title: '✕', onClick: () => sileo.dismiss(id) };
    id = sileo.info({
      title,
      description,
      styles: { badge: "bg-blue-500/10 text-blue-500!" },
      button,
    });
  }

  loading(title: string, description?: string): unknown {
    const sileoApi = sileo as unknown as { loading?: (payload: { title: string; description?: string }) => unknown };
    if (typeof sileoApi.loading === 'function') {
      return sileoApi.loading({ title, description });
    }
    sileo.info({ title, description });
    return undefined;
  }

  dismiss(toastId?: unknown): void {
    const sileoApi = sileo as unknown as { dismiss?: (id?: unknown) => void };
    if (typeof sileoApi.dismiss === 'function') {
      sileoApi.dismiss(toastId);
    }
  }

  /**
   * Show a promise toast
   */
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    }
  ) {
    sileo.promise(promise, {
      loading: { title: messages.loading },
      success: { title: typeof messages.success === 'string' ? messages.success : messages.loading },
      error: { title: typeof messages.error === 'string' ? messages.error : messages.loading }
    });

    return promise;
  }
}

export const toastService = new ToastService();
